import { Box, Button, Stack, Typography, styled } from '@mui/material';
import { ArrowForward } from '@mui/icons-material';
import { EMULATOR_LABELS, EMULATOR_TRIAL_TAGLINES, TEST_IDS } from '@localstack/constants';
import { StartPlanButton } from '@localstack/ui';
import { DocumentType, EmulatorType, LegalAcceptanceSource, Permission, PlanFamily } from '@localstack/types';
import { LegalService, SubscriptionService, useApiEffect, useApiGetter, useRoutes } from '@localstack/services';
import { ReactElement } from 'react';

import { useAuthProvider } from '~/hooks/useAuthProvider';
import { useDirectSubscribe } from '~/hooks/useDirectSubscribe';
import { pricingRoute } from '~/util/pricing';

const PromptStrip = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: theme.spacing(2),
  width: '100%',
  padding: theme.spacing(2),
  borderRadius: theme.spacing(2),
  border: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.paper,
}));

type Props = {
  /** The product the workspace is not entitled to yet. */
  emulator: EmulatorType;
};

export const UnentitledEmulatorPrompt = ({ emulator }: Props): ReactElement | null => {
  const { can } = useAuthProvider();
  const { goto } = useRoutes();
  const canSubscribe = can(Permission.CREATE_SUBSCRIPTIONS);

  const { data: plans, isLoading: isLoadingPlans } = useApiGetter(SubscriptionService, 'listPlans', [emulator], {
    enable: canSubscribe,
  });
  const { recordLegalAcceptance } = useApiEffect(LegalService, ['recordLegalAcceptance']);
  const { subscribeDirectly, isLoading } = useDirectSubscribe();

  if (!canSubscribe || isLoadingPlans) return null;

  const label = EMULATOR_LABELS[emulator];
  const startablePlan = (plans ?? []).find(
    (plan) => plan.family === PlanFamily.TRIAL_PLANS || plan.family === PlanFamily.PREVIEW_PLANS,
  );
  const isPreview = startablePlan?.family === PlanFamily.PREVIEW_PLANS;
  const startedPlan = isPreview ? `joining the ${label} public preview` : `starting a ${label} trial`;

  const startPlan = async () => {
    if (!startablePlan) return;

    const subscription = await subscribeDirectly({ planId: startablePlan.id });
    if (!subscription) return;

    await recordLegalAcceptance({
      document_type: DocumentType.MSA,
      source: LegalAcceptanceSource.GETTING_STARTED,
      context: `Implicitly accepted MSA when ${startedPlan} on the getting started page`,
    });
  };

  return (
    <PromptStrip data-testid={TEST_IDS.GETTING_STARTED_PROMPT_STRIP}>
      <Stack>
        <Typography fontWeight={600} fontSize={13}>
          Try LocalStack for {label}
        </Typography>
        <Typography fontSize={12} color="text.secondary" lineHeight={1.3}>
          {EMULATOR_TRIAL_TAGLINES[emulator]}
        </Typography>
      </Stack>
      {startablePlan ? (
        <StartPlanButton
          emulator={emulator}
          family={startablePlan.family}
          buttonText={isPreview ? 'Enable Preview' : 'Start Trial'}
          data-testid={TEST_IDS.GETTING_STARTED_PROMPT_BUTTON}
          size="small"
          disabled={isLoading}
          onClick={startPlan}
        />
      ) : (
        <Button
          variant="outlined"
          size="small"
          endIcon={<ArrowForward />}
          onClick={() => goto(pricingRoute([emulator]))}
        >
          All Plans
        </Button>
      )}
    </PromptStrip>
  );
};
